import { asDetailResponse, asPaginatedResponse, useApi, useDetailApi, usePaginatedApi } from "../../../swr/useApi";
import { adminOrderDisputesService } from "../../api_client";
import type {
  AddDisputeMessageDto,
  OrderDisputeDto,
  OrderDisputeMessageDto,
  OrderDisputeQueryDto,
  OrderDisputeStatsDto,
  ResolveDisputeDto,
} from "../../../api";
import { OrderDisputeDtoStatusEnum } from "../../../api";

export type OrderDisputeListDto = OrderDisputeDto;
export type {
  OrderDisputeDto,
  OrderDisputeMessageDto,
  OrderDisputeStatsDto,
  ResolveDisputeDto,
  AddDisputeMessageDto,
};

export type AdminOrderDisputesQuery = {
  page?: number;
  pageSize?: number;
  status?: string;
};

// Mirrors the backend state machine in order-disputes.service.ts
export const ALLOWED_TRANSITIONS: Record<string, OrderDisputeDtoStatusEnum[]> = {
  [OrderDisputeDtoStatusEnum.Open]: [
    OrderDisputeDtoStatusEnum.UnderReview,
    OrderDisputeDtoStatusEnum.Rejected,
  ],
  [OrderDisputeDtoStatusEnum.UnderReview]: [
    OrderDisputeDtoStatusEnum.ResolvedRefund,
    OrderDisputeDtoStatusEnum.ResolvedReplacement,
    OrderDisputeDtoStatusEnum.ResolvedOther,
    OrderDisputeDtoStatusEnum.Rejected,
  ],
  [OrderDisputeDtoStatusEnum.ResolvedRefund]: [],
  [OrderDisputeDtoStatusEnum.ResolvedReplacement]: [],
  [OrderDisputeDtoStatusEnum.ResolvedOther]: [],
  [OrderDisputeDtoStatusEnum.Rejected]: [],
};

export function useAdminOrderDisputes(query?: AdminOrderDisputesQuery) {
  return usePaginatedApi<OrderDisputeListDto, AdminOrderDisputesQuery>({
    key: "admin-order-disputes",
    serviceMethod: async (q) =>
      asPaginatedResponse<OrderDisputeListDto>(
        adminOrderDisputesService.adminListOrderDisputes(q as unknown as OrderDisputeQueryDto),
      ),
    query,
    options: { refreshInterval: 15_000 },
  });
}

export function useAdminOrderDisputeDetails(id: string | null) {
  return useDetailApi<OrderDisputeDto>({
    key: id ? `admin-order-dispute-${id}` : null,
    serviceMethod: () => asDetailResponse<OrderDisputeDto>(adminOrderDisputesService.adminGetOrderDisputeDetail(id!)),
  });
}

export function useAdminOrderDisputeStats() {
  return useApi<OrderDisputeStatsDto>(
    "admin-order-dispute-stats",
    () => asDetailResponse<OrderDisputeStatsDto>(adminOrderDisputesService.adminGetOrderDisputeStats()),
    { refreshInterval: 15_000 },
  );
}

export async function resolveOrderDispute(
  id: string,
  dto: ResolveDisputeDto,
): Promise<OrderDisputeDto | null> {
  try {
    const res = await adminOrderDisputesService.adminResolveOrderDispute(id, dto);
    return res.data;
  } catch {
    return null;
  }
}

export async function addAdminOrderDisputeMessage(
  id: string,
  dto: AddDisputeMessageDto,
): Promise<OrderDisputeMessageDto | null> {
  try {
    const res = await adminOrderDisputesService.adminAddOrderDisputeMessage(id, dto);
    return res.data;
  } catch {
    return null;
  }
}
